// What buffer-LOCAL name is under the cursor? (alias or CTE)
//
// PRD: Ralph/NavigationalFeatures/PRD.md § NAV1 (amended 2026-08-23) + § Slice 36.
//
// Aliases and CTEs exist only in the buffer — there is no catalog object to
// ALTER — so F2 on one is served by a REAL Monaco `WorkspaceEdit` instead of
// the rename preview dialog. This module answers "which buffer-local binding
// does the cursor name, and where is it declared"; `bufferRenamePlan.ts` turns
// that into the edit set.
//
// Position gating (why a bare name match is not enough): the resolver's
// `resolveIdentifier(kind:'alias')` walks the scope chain by NAME only — a
// column that happens to be called `u` resolves to the alias `u` just as
// happily as `u.email` does. So a cursor only counts as naming an alias when
// it sits on the alias DECLARATION token or on a dotted qualifier HEAD
// (`u.email`), and only counts as naming a CTE when it sits on the WITH-clause
// name or on a reference in a relation slot. This is the same two-layer
// (position + scope-aware resolution) discipline SED-7 uses for CTE rename.
//
// KNOWN LIMIT — non-ASCII identifiers. The resolver's bare-identifier reader
// accepts `[A-Za-z_][A-Za-z0-9_$]*`, so an unquoted Unicode alias
// (`FROM users ü`) is never read as an identifier and F2 on it answers
// `no-target`: the rename box does not open and Monaco shows the rejection
// reason. That is a safe refusal, not a wrong edit — a partial rename is the
// failure mode worth avoiding here. Quoting the alias (`"ü"`) makes it
// renamable, because a delimited identifier is read verbatim.

import type { DatabaseEngineId } from '@/core/engines';

import { buildParsedDocumentFromSource, getDocumentStructure } from '../../sql-grammar/document';
import { buildLineIndex, rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import type { IdentifierFolding, ParseOptions, SqlEngine, StatementInfo } from '../../sql-grammar/model';
import { databaseEngineIdToSqlEngine } from '../../intellisense/versionGating/engineIdMapping';
import { relationSlotKind } from '../../sql-resolver/scope/relationSlot';
import { computeCteBindingsForRange } from '../../sql-resolver/scope/cteScope';
import { foldIdentifier } from '../../sql-resolver/scope/identifiers';
import { resolveIdentifier } from '../../sql-resolver/identifier/resolveIdentifier';
import type { AliasBinding, CteBinding, ParsedBuffer } from '../../sql-resolver/types';

import { forEachIdentifierChain, type IdentifierChain } from './bufferIdentifierScan';

export type BufferRenameKind = 'alias' | 'cte';

export type BufferRenameTarget = {
  readonly kind: BufferRenameKind;
  /** Current (unquoted) name — Monaco's inline-rename placeholder. */
  readonly name: string;
  /** Byte range of the identifier token the cursor is on. */
  readonly cursorTokenRange: { readonly start: number; readonly end: number };
  /** Statement that owns the binding — the rename never leaves it. */
  readonly statement: StatementInfo;
  readonly alias?: AliasBinding;
  readonly cte?: CteBinding;
  /** Index of the CTE inside its statement's top-level WITH clause. */
  readonly cteIndex?: number;
};

export type BufferContext = {
  readonly buffer: ParsedBuffer;
  readonly options: ParseOptions;
  readonly source: string;
  /** Built once per context: `resolveAliasBinding` is called per candidate
   *  occurrence, and rebuilding an O(source) line index inside that loop turns
   *  a linear scan into a quadratic one on a large buffer. */
  readonly lineIdx: LineIndex;
};

/** Engine-correct `ParseOptions` for a renderer buffer. Mirrors the folding
 *  defaults `bufferAliasResolver` / `aliasAtCursor` / `cteAtCursor` already use
 *  so alias + CTE matching folds identically across the navigational surfaces. */
export function parseOptionsForEngine(engineId: DatabaseEngineId): ParseOptions {
  const engine = databaseEngineIdToSqlEngine(engineId) ?? 'mssql';
  return { engine, identifierFolding: defaultFoldingForEngine(engine) };
}

export function defaultFoldingForEngine(engine: SqlEngine): IdentifierFolding {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

/** Parse `source` into the resolver's `ParsedBuffer`. Returns null when the
 *  grammar cannot build a document (the caller then treats the buffer as
 *  unrewritable rather than guessing). */
export function buildBufferContext(
  source: string,
  options: ParseOptions,
): BufferContext | null {
  try {
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'nav-rename-buffer');
    return {
      buffer: { parsedDocument, documentStructure },
      options,
      source,
      lineIdx: buildLineIndex(source),
    };
  } catch {
    return null;
  }
}

export function statementAtOffset(
  ctx: BufferContext,
  offset: number,
): StatementInfo | null {
  const statements = ctx.buffer.parsedDocument.statements;
  return (
    statements.find(
      (s) => offset >= s.range.startOffset && offset <= s.range.endOffset,
    ) ?? null
  );
}

/** Resolve the buffer-local rename target at `offset`, or null when the cursor
 *  names something else (a catalog object, a keyword, a literal). */
export function findBufferRenameTarget(
  ctx: BufferContext,
  offset: number,
): BufferRenameTarget | null {
  const statement = statementAtOffset(ctx, offset);
  if (!statement) return null;
  const chain = chainAtOffset(ctx, statement, offset);
  if (!chain) return null;

  const aliasHit = aliasTargetFor(ctx, chain, offset, statement);
  if (aliasHit) return aliasHit;
  return cteTargetFor(ctx, chain, offset, statement);
}

/** The identifier chain whose HEAD token contains `offset`. Only the head is
 *  considered: a cursor on `email` in `u.email` names a column, not the alias. */
function chainAtOffset(
  ctx: BufferContext,
  statement: StatementInfo,
  offset: number,
): IdentifierChain | null {
  let found: IdentifierChain | null = null;
  forEachIdentifierChain(
    ctx.source,
    ctx.options,
    statement.range.startOffset,
    statement.range.endOffset,
    (chain) => {
      if (chain.head.start > offset) return false;
      if (offset >= chain.head.start && offset <= chain.head.end) {
        found = chain;
        return false;
      }
      return true;
    },
  );
  return found;
}

function aliasTargetFor(
  ctx: BufferContext,
  chain: IdentifierChain,
  offset: number,
  statement: StatementInfo,
): BufferRenameTarget | null {
  const binding = resolveAliasBinding(ctx, chain);
  if (!binding) return null;
  const declStart = binding.range.startOffset;
  const declEnd = binding.range.endOffset;
  const onDeclaration = offset >= declStart && offset <= declEnd;
  const onQualifierHead =
    chain.partCount >= 2 &&
    relationSlotKind(ctx.source, chain.head.start, chain.head.end) === 'dotted-head';
  if (!onDeclaration && !onQualifierHead) return null;
  return {
    kind: 'alias',
    name: binding.alias,
    cursorTokenRange: { start: chain.head.start, end: chain.head.end },
    statement,
    alias: binding,
  };
}

/** Scope-aware alias lookup for a chain head. */
export function resolveAliasBinding(
  ctx: BufferContext,
  chain: IdentifierChain,
): AliasBinding | null {
  const hit = resolveIdentifier(ctx.buffer, {
    qualified: {
      name: chain.head.value,
      raw: chain.head.value,
      quoted: chain.head.quoted,
      foldedName: foldFor(ctx, chain.head.value, chain.head.quoted),
    },
    range: rangeFromOffsets(ctx.lineIdx, chain.head.start, chain.head.end),
    kind: 'alias',
  });
  return hit && hit.kind === 'alias' ? hit.target : null;
}

function cteTargetFor(
  ctx: BufferContext,
  chain: IdentifierChain,
  offset: number,
  statement: StatementInfo,
): BufferRenameTarget | null {
  if (chain.partCount !== 1 && !isDottedHead(ctx, chain)) return null;
  const ctes = computeCteBindingsForRange(
    ctx.buffer,
    statement.range.startOffset,
    statement.range.endOffset,
  );
  if (ctes.length === 0) return null;
  const folded = foldFor(ctx, chain.head.value, chain.head.quoted);
  const index = ctes.findIndex((c) => c.name.foldedName === folded);
  if (index < 0) return null;
  const cte = ctes[index];
  // `nameRange` is optional on the type only so hand-built test fixtures stay
  // valid; `cteScope.ts` always populates it for a parsed buffer.
  const nameRange = cte.nameRange;
  const onDeclaration =
    nameRange !== undefined &&
    offset >= nameRange.startOffset &&
    offset <= nameRange.endOffset;
  const slot = relationSlotKind(ctx.source, chain.head.start, chain.head.end);
  if (!onDeclaration && slot === 'none') return null;
  return {
    kind: 'cte',
    name: cte.name.name,
    cursorTokenRange: { start: chain.head.start, end: chain.head.end },
    statement,
    cte,
    cteIndex: index,
  };
}

function isDottedHead(ctx: BufferContext, chain: IdentifierChain): boolean {
  return relationSlotKind(ctx.source, chain.head.start, chain.head.end) === 'dotted-head';
}

export function foldFor(ctx: BufferContext, value: string, quoted: boolean): string {
  return foldIdentifier(value, quoted, ctx.options);
}
